/**
 * Preview Modal — full product detail view opened from the right-rail
 * preview's expand button. Ported from the original designer
 * (global-functions.js openPreviewModal), modernized: store-driven product
 * info instead of window globals, Phosphor icons, brand-token styles.
 * The original's thumbs strip was a never-populated placeholder and is
 * intentionally not reproduced.
 */

import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { handleShareBuyClick } from './share-buy/index.js'

function modalEl() {
  return document.getElementById('previewModal')
}

function currentProductInfo() {
  const current = store.getState('design.currentProduct')
  const sdkProduct = store.getState('design.sdkInstance')?.product
  return {
    name:
      current?.name ||
      sdkProduct?.english_name ||
      sdkProduct?.name ||
      '',
    price: current?.price ?? sdkProduct?.currentPrice ?? null,
    variants: current?.variants || [],
  }
}

function renderVariantPills(containerId, values) {
  const el = document.getElementById(containerId)
  if (!el) return
  if (!values.length) {
    el.innerHTML = '<span class="design-variant-empty">—</span>'
    return
  }
  el.innerHTML = values
    .map((v) => `<span class="variant-option">${String(v).replace(/[<>&"]/g, '')}</span>`)
    .join('')
}

export function openPreviewModal() {
  const modal = modalEl()
  if (!modal) return

  // The SDK renders the preview as a <canvas> (1000px) inside
  // #previewBoxContent — snapshot it; fall back to an <img> if a future SDK
  // version renders one instead.
  // The SDK's mount point inside #previewBox varies between renders
  // (sometimes inside #previewBoxContent, sometimes a sibling) — search the
  // whole box.
  let src = null
  const canvas = document.querySelector('#previewBox canvas')
  if (canvas) {
    try {
      src = canvas.toDataURL('image/png')
    } catch { /* tainted canvas — fall through to img */ }
  }
  if (!src) src = document.querySelector('#previewBox img')?.src ?? null
  if (!src) {
    eventBus.emit(EVENTS.TOAST_SHOW, { message: 'No preview available yet', type: 'warning' })
    return
  }

  const img = document.getElementById('previewModalImage')
  if (img) img.src = src

  const info = currentProductInfo()
  const nameEl = document.getElementById('previewModalProductName')
  if (nameEl) nameEl.textContent = info.name || 'Product preview'
  const priceEl = document.getElementById('previewModalPrice')
  if (priceEl) priceEl.textContent = info.price ? `$${Number(info.price).toFixed(2)}` : ''

  const colors = []
  const sizes = []
  const seenC = new Set()
  const seenS = new Set()
  for (const v of info.variants) {
    const color = typeof v.color === 'object' ? v.color?.color_name || v.color?.name : v.color
    const size = typeof v.size === 'object' ? v.size?.name || v.size?.title : v.size
    if (color && !seenC.has(color)) {
      seenC.add(color)
      colors.push(color)
    }
    if (size && !seenS.has(size)) {
      seenS.add(size)
      sizes.push(size)
    }
  }
  renderVariantPills('previewModalColors', colors)
  renderVariantPills('previewModalSizes', sizes)

  modal.dataset.open = 'true'
  modal.setAttribute('aria-hidden', 'false')
}

export function closePreviewModal() {
  const modal = modalEl()
  if (!modal) return
  modal.dataset.open = 'false'
  modal.setAttribute('aria-hidden', 'true')
}

/** Idempotent — bind expand/close/backdrop/ESC + in-modal Share&Buy. */
export function initPreviewModal() {
  const modal = modalEl()
  if (!modal || modal.dataset.bound) return
  modal.dataset.bound = '1'

  document.getElementById('previewExpandBtn')?.addEventListener('click', openPreviewModal)
  document.getElementById('previewModalClose')?.addEventListener('click', closePreviewModal)
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closePreviewModal()
  })
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.dataset.open === 'true') closePreviewModal()
  })
  document.getElementById('previewModalShareBuy')?.addEventListener('click', (e) => {
    closePreviewModal()
    handleShareBuyClick(e)
  })
}
